import { cn } from 'shadcn/lib/utils';

/**
 * Displays a value or "None" if the value is falsy.
 *
 * @param {string} value - The value to display.
 */
function displayOrNone(value) {
    return value || <span className="italic text-muted-foreground">None</span>;
}

/**
 * Summary view for the banner editor.
 *
 * @param {Object} data - Non-configurable banner data.
 * @param {Object} formData - Configurable banner data.
 */
export default function BannerEditorSummary({ data, formData }) {
    return (
        <div className="grid grid-cols-[max-content_1fr] gap-y-1 gap-x-8 items-baseline">
            <Row label="id">
                <p className="text-sm">{displayOrNone(data.id)}</p>
            </Row>
            <Row label="Active">
                <p className="text-sm">{formData.active ? 'Yes' : 'No'}</p>
            </Row>
            <Row label="Images (large, small)" labelClass="mb-0.5">
                <ImagePair largeImage={formData.largeImage} smallImage={formData.smallImage} />
            </Row>
            <Row label="Name">
                <p className="text-sm">{displayOrNone(formData.bannerName)}</p>
            </Row>
            <Row label="Copy">
                <p className="text-sm">{displayOrNone(formData.copy)}</p>
            </Row>
            <Row label="Link URL">
                <p className="text-sm">{displayOrNone(formData.linkURL)}</p>
            </Row>
            <Row label="Link Text">
                <p className="text-sm">{displayOrNone(formData.linkTitle)}</p>
            </Row>
            <Row label="Background Color" labelClass="transform -translate-y-1">
                <ColorSwatch color={formData.backgroundColor} />
            </Row>
        </div>
    );
}

/**
 * Displays a row of label and value.
 *
 * @param {string} label - The label to display.
 * @param {React.ReactNode} children - The value to display.
 * @param {string} labelClass - The class name for the label.
 * @param {string} valueClass - The class name for the value.
 */
function Row({ children, label, labelClass = '', valueClass = '' }) {
    return (
        <>
            <p className={cn('text-xs font-medium text-muted-foreground', labelClass)}>{label}</p>
            <div className={valueClass}>{children}</div>
        </>
    );
}

/**
 * Displays a pair of images.
 *
 * @param {string} largeImage - The URL of the large image.
 * @param {string} smallImage - The URL of the small image.
 */
function ImagePair({ largeImage, smallImage }) {
    const BASE_URL = 'https://img-cdn.misfitsmarket.com/';
    return (
        <div className="flex gap-2">
            {largeImage ? (
                <img alt="" className="max-w-60 h-15" src={`${BASE_URL}${largeImage}`} />
            ) : (
                <span className="italic text-muted-foreground">None</span>
            )}
            {smallImage ? (
                <img alt="" className="max-w-60 h-15" src={`${BASE_URL}${smallImage}`} />
            ) : (
                <span className="italic text-muted-foreground">None</span>
            )}
        </div>
    );
}

/**
 * Displays a color swatch.
 *
 * @param {string} color - The color to display.
 */
function ColorSwatch({ color }) {
    return (
        <div className="flex items-center gap-2">
            <div className="size-4 rounded border" style={{ backgroundColor: color || '#FFFFFF' }} />
            <p className="text-sm text-left">{displayOrNone(color)}</p>
        </div>
    );
}
